"use client";

import { useState, Suspense } from "react";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";

function LoginForm() {
  const router = useRouter();
  const sp = useSearchParams();
  let rawCallback = sp.get("callbackUrl") || "/dashboard";
  // Ensure callback always lives under /v2 basePath
  if (rawCallback.startsWith("/v2")) rawCallback = rawCallback.substring(3);
  if (!rawCallback.startsWith("/")) rawCallback = "/" + rawCallback;
  const internalPath = rawCallback;
  const callbackUrl = "/v2" + internalPath;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Manual fetch to avoid next-auth/react basePath redirect issues
      const csrfRes = await fetch("/v2/api/auth/csrf");
      const { csrfToken } = await csrfRes.json();

      const formBody = new URLSearchParams({
        csrfToken,
        email,
        password,
        callbackUrl: callbackUrl,
        json: "true",
      });

      const res = await fetch("/v2/api/auth/callback/credentials", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody.toString(),
        redirect: "manual",
      });

      if (res.status === 0 || res.type === "opaqueredirect") {
        window.location.href = callbackUrl;
        return;
      }

      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await res.json();
        if (data.url && data.url.includes("error")) {
          setError("Invalid email or password");
          setSubmitting(false);
          return;
        }
        if (data.error) {
          setError("Invalid email or password");
          setSubmitting(false);
          return;
        }
      }
      window.location.href = callbackUrl;
    } catch (e: any) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <>
      {error && (
        <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs text-white/60 font-medium block mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            className="w-full bg-[#0D0D0D] border border-white/[0.06] focus:border-[#FFC107] outline-none rounded-lg px-4 py-3 text-sm transition"
          />
        </div>
        <div>
          <label className="text-xs text-white/60 font-medium block mb-2">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full bg-[#0D0D0D] border border-white/[0.06] focus:border-[#FFC107] outline-none rounded-lg px-4 py-3 text-sm transition"
          />
        </div>

        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="blipo-btn w-full py-3.5 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {submitting ? "Signing in..." : "Sign in →"}
        </button>
      </form>
    </>
  );
}

export default function LoginPage() {
  return (
    <div className="min-h-screen blipo-grid-bg flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-baseline gap-1.5 mb-2">
            <h1 className="text-4xl font-bold tracking-tight text-[#FFC107] lowercase leading-none">
              blipo
            </h1>
            <span className="text-[11px] tracking-[0.18em] text-white/40 uppercase font-medium">
              studios
            </span>
          </div>
          <p className="text-sm text-white/50">Welcome back</p>
        </div>

        <div className="blipo-card p-7">
          <h2 className="text-2xl font-bold mb-1">Sign in</h2>
          <p className="text-sm text-white/50 mb-6">Access your avatar studio</p>

          <Suspense fallback={<div className="text-white/40 text-sm">Loading...</div>}>
            <LoginForm />
          </Suspense>

          <div className="mt-6 pt-6 border-t border-white/[0.06] text-center">
            <p className="text-xs text-white/40">
              Don't have an account?{" "}
              <Link href="/signup" className="text-[#FFC107] hover:text-[#FFD24D] font-semibold">
                Create one
              </Link>
            </p>
          </div>
        </div>

        <p className="text-[10px] text-white/30 text-center mt-6 tracking-wider uppercase">
          Powered by BLIPO Studios
        </p>
      </div>
    </div>
  );
}
